"use client";

import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { MovingAverageStrings } from "@/components/tools/moving-average-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  formatStat,
  machineNumber,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  alphaFromSpan,
  forecastAccuracy,
  kernel,
  latestAverage,
  type MovingAverageOptions,
  movingAverage,
} from "@/lib/tool-kit/stats-regression/moving-average";

interface Props {
  values: number[];
  labels: string[];
  options: MovingAverageOptions;
  strings: MovingAverageStrings;
  fmt: NumberFormatter;
}

const SHOWN_ROWS = 40;
const CSV_SPECIAL = /[",\n]/;

function csvCell(value: string): string {
  return CSV_SPECIAL.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** The first average written out with the reader's numbers. */
function firstAverage(
  values: readonly number[],
  averages: readonly (number | null)[],
  options: MovingAverageOptions,
  fmt: NumberFormatter
): { formula: string; value: string } | null {
  const num = (value: number) => formatStat(fmt, value);
  const first = averages.findIndex((value) => value !== null);
  if (first < 0) {
    return null;
  }
  const value = num(averages[first] as number);
  if (options.type === "exponential") {
    const alpha = options.alpha ?? alphaFromSpan(options.window);
    if (first + 1 >= values.length) {
      return null;
    }
    const t0 = first + 1;
    const here = averages[t0] as number;
    return {
      formula: `s${t0 + 1} = α × y${t0 + 1} + (1 − α) × s${t0} = ${num(alpha)} × ${num(values[t0])} + ${num(1 - alpha)} × ${num(averages[first] as number)}`,
      value: num(here),
    };
  }
  const points = kernel(options).filter(
    ({ offset }) => first + offset >= 0 && first + offset < values.length
  );
  const total = points.reduce((sum, p) => sum + p.weight, 0);
  const numerator = points
    .map((p) =>
      p.weight === 1
        ? num(values[first + p.offset])
        : `${num(p.weight)} × ${num(values[first + p.offset])}`
    )
    .join(" + ");
  return { formula: `(${numerator}) ÷ ${num(total)}`, value };
}

function Cards({
  values,
  averages,
  options,
  strings,
  fmt,
}: Props & { averages: (number | null)[] }) {
  const num = (value: number) => formatStat(fmt, value);
  const latest = latestAverage(averages);
  const computed = averages.filter((value) => value !== null).length;
  const forecasts =
    options.alignment === "trailing" || options.type === "exponential";
  const accuracy = forecasts ? forecastAccuracy(values, averages) : null;
  const alpha = options.alpha ?? alphaFromSpan(options.window);
  return (
    <div aria-live="polite" className="space-y-4">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.latestCard}
          value={latest === null ? "—" : num(latest)}
        />
        {forecasts && latest !== null ? (
          <StatCard label={strings.nextForecast} value={num(latest)} />
        ) : null}
        <StatCard label={strings.observationsCard} value={fmt(values.length)} />
        <StatCard label={strings.averagesCard} value={fmt(computed)} />
        {options.type === "exponential" ? (
          <StatCard label={strings.alphaCard} value={num(alpha)} />
        ) : null}
        {accuracy ? (
          <>
            <StatCard label={strings.maeCard} value={num(accuracy.mae)} />
            <StatCard label={strings.rmseCard} value={num(accuracy.rmse)} />
            {Number.isNaN(accuracy.mape) ? null : (
              <StatCard
                label={strings.mapeCard}
                value={`${num(accuracy.mape)}%`}
              />
            )}
          </>
        ) : null}
      </StatGrid>
      {accuracy ? (
        <Explanation>
          {t(strings.accuracyNote, { n: fmt(accuracy.n) })}
        </Explanation>
      ) : null}
      {forecasts ? null : (
        <Explanation>
          {strings.centredNote}
          {options.type === "simple" && options.window % 2 === 0
            ? ` ${strings.evenCentredNote}`
            : ""}
        </Explanation>
      )}
    </div>
  );
}

export function MovingAverageResults({
  values,
  labels,
  options,
  strings,
  fmt,
}: Props) {
  const num = (value: number) => formatStat(fmt, value);
  const averages = movingAverage(values, options);
  const rowLabel = (i: number) => (labels[i] ? labels[i] : fmt(i + 1));
  const defined = averages.flatMap((value, i) => (value === null ? [] : [i]));
  const tsv = [
    [strings.colLabel, strings.colValue, strings.colAverage].join("\t"),
    ...values.map((value, i) =>
      [
        rowLabel(i),
        machineNumber(value, 6),
        averages[i] === null ? "" : machineNumber(averages[i] as number, 6),
      ].join("\t")
    ),
  ].join("\n");
  const csv = [
    [strings.colLabel, strings.colValue, strings.colAverage]
      .map(csvCell)
      .join(","),
    ...values.map((value, i) =>
      [
        csvCell(rowLabel(i)),
        machineNumber(value, 6),
        averages[i] === null ? "" : machineNumber(averages[i] as number, 6),
      ].join(",")
    ),
  ].join("\n");
  const first = firstAverage(values, averages, options, fmt);
  const weightsShown =
    options.type === "weighted" || options.alignment === "centred"
      ? kernel(options).map((p) => num(p.weight))
      : [];
  const methodText = {
    simple: t(strings.workSimple, { k: fmt(options.window) }),
    weighted: strings.workWeighted,
    exponential: t(strings.workExponential, {
      alpha: num(options.alpha ?? alphaFromSpan(options.window)),
    }),
  }[options.type];
  const shown = values.slice(0, SHOWN_ROWS);
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Cards
        averages={averages}
        fmt={fmt}
        labels={labels}
        options={options}
        strings={strings}
        values={values}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
        <XYPlot
          fmt={fmt}
          label={t(strings.plotLabel, { n: fmt(values.length) })}
          series={[
            {
              kind: "line",
              xs: values.map((_, i) => i + 1),
              ys: values,
              color: 0,
              label: strings.seriesData,
              dashed: true,
            },
            {
              kind: "line",
              xs: defined.map((i) => i + 1),
              ys: defined.map((i) => averages[i] as number),
              color: 1,
              label: strings.seriesAverage,
            },
          ]}
          xLabel={strings.axisX}
          yLabel={strings.axisY}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableTitle}</h3>
        <ResultTable
          head={[
            strings.colIndex,
            strings.colLabel,
            strings.colValue,
            strings.colAverage,
          ]}
          rows={shown.map((value, i) => [
            fmt(i + 1),
            labels[i] ?? "",
            num(value),
            averages[i] === null ? "—" : num(averages[i] as number),
          ])}
        />
        {values.length > shown.length ? (
          <Explanation>
            {t(strings.tableMore, {
              shown: fmt(shown.length),
              total: fmt(values.length),
            })}
          </Explanation>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyTable}
            small
            text={tsv}
          />
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              downloadText(strings.csvFile, csv, "text/csv;charset=utf-8")
            }
            type="button"
          >
            {strings.downloadCsv}
          </button>
        </div>
      </div>
      <WorkingPanel title={strings.workingTitle}>
        <Explanation>{methodText}</Explanation>
        {weightsShown.length > 0 && options.type !== "exponential" ? (
          <Explanation>
            {t(strings.weightsUsed, {
              weights: weightsShown.join(strings.weightsSeparator),
            })}
          </Explanation>
        ) : null}
        {first ? (
          <Formula>
            {t(strings.workFirst, {
              formula: first.formula,
              value: first.value,
            })}
          </Formula>
        ) : null}
      </WorkingPanel>
    </section>
  );
}
